import { useEffect, useRef, type ReactNode } from 'react';
import './Dialog.css';

interface Props {
  title: string;
  /** Shown under the title — what this dialog is for, in one line. */
  hint?: string;
  submitLabel?: string;
  submitDisabled?: boolean;
  onSubmit: () => void;
  onClose: () => void;
  children: ReactNode;
}

/**
 * A small centred window over a dimmed backdrop.
 *
 * One of the few genuinely floating things in the app, so one of the few with
 * a shadow. Escape closes, Enter submits, and focus is trapped inside while it
 * is open — it should be dismissable without reaching for the mouse.
 */
export function Dialog({
  title,
  hint,
  submitLabel = 'Save',
  submitDisabled = false,
  onSubmit,
  onClose,
  children,
}: Props) {
  const root = useRef<HTMLFormElement>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
        return;
      }
      if (e.key !== 'Tab' || !root.current) return;

      const focusable = root.current.querySelectorAll<HTMLElement>(
        'input, select, textarea, button:not(:disabled), [tabindex]:not([tabindex="-1"])',
      );
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (!first || !last) return;

      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [onClose]);

  return (
    <div className="dialog__scrim" onMouseDown={onClose}>
      <form
        className="dialog"
        ref={root}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onMouseDown={(e) => e.stopPropagation()}
        onSubmit={(e) => {
          e.preventDefault();
          if (!submitDisabled) onSubmit();
        }}
      >
        <div className="dialog__head">
          <span className="dialog__title">{title}</span>
          {hint && <span className="dialog__hint">{hint}</span>}
        </div>

        <div className="dialog__body">{children}</div>

        <div className="dialog__foot">
          <span className="kbd">esc to cancel</span>
          <span className="spacer" />
          <button type="button" className="dialog__button" onClick={onClose}>
            Cancel
          </button>
          <button
            type="submit"
            className="dialog__button dialog__button--primary"
            disabled={submitDisabled}
          >
            {submitLabel}
          </button>
        </div>
      </form>
    </div>
  );
}
